// Section 3: Underwriting Team Appointment.
//
// A1 assigns A2 / A3 / A4 (or marks them Not Applicable) from inside the
// section. The pill widget in the header refreshes within ~1s because
// useUpdateAssignment invalidates dealFlowKeys.assignments(dealId) on
// success. A live preview row at the bottom mirrors the pill state so
// the analyst sees the same data without scrolling.

import { useEffect, useState } from "react";
import { Button, Card, FormField } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { RoleFilteredUserPicker } from "../../common/RoleFilteredUserPicker";
import { DealAssignmentPills } from "../../DealAssignmentPills";
import {
  useDealAssignments,
  useMarkSlotNotApplicable,
  useUpdateAssignment,
} from "@/hooks/dealFlow/useDealFlow";
import type {
  DealAssignmentStatus,
  DealRoleCode,
} from "@/api/dealFlow/types";
import type { DealPipelineStage } from "@/types/deals";

interface UnderwritingTeamAppointmentSectionProps {
  dealId: string;
  dealStage: DealPipelineStage;
}

const SLOT_ROLES: ReadonlyArray<{ code: DealRoleCode; label: string }> = [
  { code: "A2", label: "A2 — Financial Analyst" },
  { code: "A3", label: "A3 — Legal Analyst" },
  { code: "A4", label: "A4 — Market & Credit Research" },
];

interface SlotDraft {
  userId: string | null;
  status: DealAssignmentStatus;
}

export function UnderwritingTeamAppointmentSection({
  dealId,
  dealStage,
}: UnderwritingTeamAppointmentSectionProps) {
  const assignments = useDealAssignments(dealId);
  const update = useUpdateAssignment(dealId);
  const markNa = useMarkSlotNotApplicable(dealId);
  const toast = useToast();

  const [drafts, setDrafts] = useState<Record<DealRoleCode, SlotDraft>>({
    A2: { userId: null, status: "Unassigned" },
    A3: { userId: null, status: "Unassigned" },
    A4: { userId: null, status: "Unassigned" },
  } as Record<DealRoleCode, SlotDraft>);

  // Hydrate drafts from server state.
  useEffect(() => {
    if (!assignments.data) return;
    setDrafts((prev) => {
      const next = { ...prev };
      for (const role of SLOT_ROLES) {
        const a = assignments.data?.find((x) => x.roleCode === role.code);
        next[role.code] = {
          userId: a?.assignedUserId ?? null,
          status: a?.status ?? "Unassigned",
        };
      }
      return next;
    });
  }, [assignments.data]);

  const onSaveAll = async () => {
    try {
      for (const role of SLOT_ROLES) {
        const draft = drafts[role.code];
        if (draft.status === "NotApplicable") {
          await markNa.mutateAsync(role.code);
        } else if (draft.status === "Assigned" && draft.userId) {
          await update.mutateAsync({
            roleCode: role.code,
            body: { status: "Assigned", assignedUserId: draft.userId },
          });
        } else {
          await update.mutateAsync({
            roleCode: role.code,
            body: { status: "Unassigned", assignedUserId: null },
          });
        }
      }
      toast.success("Team assignments saved.");
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not save assignments.",
      );
    }
  };

  return (
    <Card>
      <header className="mb-space-3">
        <h3 className="font-display text-h4 text-onyx m-0">
          Section 3 — Underwriting Team Appointment
        </h3>
        <p className="text-caption text-gray-mid m-0">
          Assign A2 / A3 / A4 (or mark Not Applicable) before the Criteria
          Checklist becomes editable. Header pill widget refreshes as you save.
        </p>
      </header>

      <div className="flex flex-col gap-space-3 mb-space-4">
        {SLOT_ROLES.map((role) => (
          <SlotRow
            key={role.code}
            roleLabel={role.label}
            roleCode={role.code}
            draft={drafts[role.code]}
            onUserChange={(uid) =>
              setDrafts((prev) => ({
                ...prev,
                [role.code]: {
                  status: uid ? "Assigned" : "Unassigned",
                  userId: uid,
                },
              }))
            }
            onToggleNa={() =>
              setDrafts((prev) => ({
                ...prev,
                [role.code]: {
                  status:
                    prev[role.code].status === "NotApplicable"
                      ? "Unassigned"
                      : "NotApplicable",
                  userId: null,
                },
              }))
            }
          />
        ))}
      </div>

      <div className="flex justify-end mb-space-4">
        <Button
          variant="primary"
          onClick={() => void onSaveAll()}
          disabled={update.isPending || markNa.isPending}
        >
          {update.isPending || markNa.isPending
            ? "Saving…"
            : "Save assignments"}
        </Button>
      </div>

      <div className="border-t border-border-light pt-space-3">
        <h4 className="font-display text-label uppercase tracking-label text-gold-shadow m-0 mb-space-2">
          Live preview
        </h4>
        <DealAssignmentPills dealId={dealId} dealStage={dealStage} />
      </div>
    </Card>
  );
}

interface SlotRowProps {
  roleCode: DealRoleCode;
  roleLabel: string;
  draft: SlotDraft;
  onUserChange: (uid: string | null) => void;
  onToggleNa: () => void;
}

function SlotRow({
  roleCode,
  roleLabel,
  draft,
  onUserChange,
  onToggleNa,
}: SlotRowProps) {
  const isNa = draft.status === "NotApplicable";
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1.5fr_2fr_auto] items-end gap-space-3">
      <div className="text-small text-onyx font-medium">{roleLabel}</div>
      <FormField label="User">
        <RoleFilteredUserPicker
          role={roleCode}
          value={draft.userId}
          onChange={onUserChange}
          disabled={isNa}
          placeholder={isNa ? "Marked Not Applicable" : "Select user…"}
        />
      </FormField>
      <label className="flex items-center gap-space-2 text-caption">
        <input type="checkbox" checked={isNa} onChange={onToggleNa} />
        Not applicable
      </label>
    </div>
  );
}

